import createModule from "../createModule"
import view from "./view"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getSelectedModelId, getShowNotHeld, getIsExpanded, getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { getBasePositionSetWithSegment, getBaseSegmentSignature } from "selectors/base"
import { getSortedContributors, getAvailableRollups, getSelectedGroup, getSortedGroupContributors, getContributorsSummaryText, getCsvData, getSelectedGroupValue, getGroupBy, getIncludeNotHeld, getSelectedGroupSiblings } from "./selectors"
import { getGroupsDropdownColumns } from "./selectors/group"
import { getEffectiveSegment } from "modules/analyze-header/selectors/effective-segment"
import { classificationsQuery, generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { getIsOpPro } from "modules/account-root/selectors"
import { getQueryGroupBy } from "helpers/rollups"
import { VIEW_TYPES } from "helpers/base"
import { getFactorSensitivityQueryParams } from "helpers/factorSensitivity"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    portfolioId: state.global.selected.portfolio,
    modelId: getSelectedModelId(state, props),
    base: getBasePositionSetWithSegment(state, props),
    baseSegmentSignature: getBaseSegmentSignature(state, props),
    segment: getEffectiveSegment(state, props),
    classifications: getClassifications(state, props),
    includeNotHeld: getIncludeNotHeld(state, props),
    expand: getIsExpanded(state, props),
    currency: getCurrency(state, props),
    factorSensitivityType: props.factorSensitivityType,
  }),
  required: ["portfolioId", "account"],
  data: [{
    resource: "factorSensitivityContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, viewType, expand, base, baseSegmentSignature, segment, classifications, includeNotHeld, factorSensitivityType, currency }) => {
      if (selectedDate) {
        return {
          id: `factorSensitivityContributors-${factorSensitivityType}${modelId}${portfolioId}${selectedDate}${viewType}${expand}${baseSegmentSignature}${JSON.stringify(segment)}${includeNotHeld}${currency}`,
          query: `query ($modelId: String!, $portfolioId: String!, $date: Date!, $expand: Boolean, $base: [PositionSetInput], $segment: Segment, $includeNotHeld: Boolean, $factorSensitivityType: FactorSensitivityType!, $stdDev: PositiveFloat, $days: PositiveInt, $horizon: FactorSensitivityTimeHorizon!) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, base: $base, interval: END_DATE) {
                factorSensitivityContributors (type: $factorSensitivityType, stdDev: $stdDev, days: $days, horizon: $horizon, includeNotHeld: $includeNotHeld) {
                  id
                  assetClass
                  assetSubclass
                  notHeld
                  country
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  currency
                  description
                  percentEquity
                  bloombergTicker
                  modelProviderId
                  factors {
                    id
                    category
                    sensitivity
                  }
                }
              }
            }
          }`,
          variables: {
            modelId,
            portfolioId,
            date: selectedDate,
            base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
            segment,
            includeNotHeld,
            expand,
            ...getFactorSensitivityQueryParams(factorSensitivityType),
          },
        }
      } else {
        return null
      }
    },
  }, {
    resource: "groupedFactorSensitivityContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, viewType, expand, base, baseSegmentSignature, contributorsGrouping, segment, classifications, includeNotHeld, factorSensitivityType, currency }) => {
      if (!contributorsGrouping) {
        return null
      }

      const queryGroupBy = getQueryGroupBy(contributorsGrouping, viewType === VIEW_TYPES.ACTIVE)

      return {
        id: `groupedFactorSensitivityContributors-${factorSensitivityType}${modelId}${portfolioId}${selectedDate}${viewType}${expand}${baseSegmentSignature}${JSON.stringify(queryGroupBy)}${JSON.stringify(segment)}${includeNotHeld}${currency}`,
        query: `query ($modelId: String!, $portfolioId: String!, $date: Date!, $expand: Boolean, $base: [PositionSetInput], $segment: Segment, $includeNotHeld: Boolean, $factorSensitivityType: FactorSensitivityType!, $stdDev: PositiveFloat, $days: PositiveInt, $groupBy: ContributorGroupType!, $groupById: String, $tier: String) {
          model (id: $modelId) {
            simulation (positionSet: {
              type: PORTFOLIO,
              id: $portfolioId,
              segment: $segment,
              expand: $expand
            }, from: $date, to: $date, base: $base, interval: END_DATE) {
              groupedFactorSensitivityContributors (type: $factorSensitivityType, stdDev: $stdDev, days: $days, horizon: MONTHLY, includeNotHeld: $includeNotHeld, groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
                name
                id
                totalPercentEquity
                factors {
                  id
                  category
                  sensitivity
                }
                contributors {
                  id
                  assetClass
                  assetSubclass
                  notHeld
                  country
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  currency
                  description
                  percentEquity
                  bloombergTicker
                  modelProviderId
                  factors {
                    id
                    category
                    sensitivity
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          portfolioId,
          date: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          ...queryGroupBy,
          ...getFactorSensitivityQueryParams(factorSensitivityType),
        },
      }
    },
  }, {
    resource: "classifications",
    as: null,
    url: () => {
      return {
        id: "classifications",
        query: classificationsQuery,
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    data: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    csvData: getCsvData(state, props),
    selectedGroup: getSelectedGroup(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    isOpPro: getIsOpPro(state, props),
    showNotHeld: getShowNotHeld(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupBy: getGroupBy(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
    ...state.modules.factorSensitivityContributors,
  }),
  actions: {
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
